import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * PD-17142 — the sales of each past day, added up from the invoice lines, so
 * a sales report reads a year from here instead of adding up every line of
 * every invoice on each load (Macomb: about 1.1 million lines a year).
 *
 * One row per day, customer, item, invoice rep, company, invoice type,
 * cancelled flag, has-sales flag and order origin, over the lines that count
 * toward sales (`invoice_detail.excludeFromSales = 0`) of invoices that are
 * not deleted.
 * Values are stored exactly as on the invoice lines: no credit-memo sign
 * flip (the invoice `type` is a column) and cancelled invoices kept (the
 * `isCancelled` column), so each report keeps its own rule for both.
 * Customers that are later deleted keep their rows; reports drop them at
 * query time by joining `client`, as they do when reading invoices.
 *
 * Only ids are stored. Names, categories, product groups and teams are
 * joined when a report runs, so a rename or a regrouping shows at once.
 *
 * The summary covers a contiguous range of days that ends before today;
 * which range, and which days inside it are waiting to be rebuilt, is kept
 * per tenant by crm-api's SalesSummaryStateService. Reports read the days
 * outside that range, and the waiting ones, from the invoices themselves
 * (see `planSalesSourceSegments` in crm-api).
 *
 * Every measure is a SUM of the line column it names, NULL when every line
 * of the row has NULL there, exactly as SUM() over the lines would be, so
 * adding the rows up again gives the same figure as adding up the lines.
 * The decimal columns keep the full scale of their sums for the same
 * reason.
 *
 * Not audited (`auditable: false`): it is a derived cache that the sales
 * summary queue rebuilds a day at a time (delete, then insert), not a record
 * anyone edits. Audited, every rebuilt row would be written to `_audit` one
 * by one.
 *
 * SM1 exposure: none. SM1 names no such table.
 */
@Entity('sales_summary_line_day', {}, { auditable: false })
// The scans of Sales Comparison by Customer and Sales by Customer and Product
// Group: a date range, then the cancelled flag and company they filter on,
// then everything they read (customer, rep, item, latest invoice, the four
// measures), so a year is read from the index alone, in date order.
@Index('sales_summary_line_day__date_sales', [
	'date',
	'isCancelled',
	'fkCompanyId',
	'fkClientId',
	'fkUserId',
	'fkItemId',
	'maxInvoiceId',
	'subtotal',
	'knownCostSubtotal',
	'knownCostGrossProfit',
	'costTimesQuantity',
])
// One customer's history, and the reports filtered to picked customers.
@Index('sales_summary_line_day__fk_client__id_date', ['fkClientId', 'date'])
// The days that sold an item: the refresh rebuilds them when the item's
// category or another grouping attribute changes, and the item reports read
// one item's history.
@Index('sales_summary_line_day__fk_item__id_date', ['fkItemId', 'date'])
export class SalesSummaryLineDayEntity extends BaseEntity {
	/** The invoice date (`invoice.date`). */
	@Column('date', { name: 'date' })
	public date!: string;

	/** `invoice._fk_client__id`. */
	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	/** `invoice_detail._fk_item__id`; NULL for lines with no item (freight, charges). */
	@Column('int', { name: 'fk_item__id', unsigned: true, nullable: true })
	public fkItemId!: number | null;

	/**
	 * The rep the invoice is credited to (`invoice.fk_user__id`, a
	 * tenant-local user id), NULL and 0 kept apart exactly as on the invoice.
	 * Named like the invoice column so the reports' data scope applies to
	 * either source unchanged.
	 */
	@Column('int', { name: 'fk_user__id', unsigned: true, nullable: true })
	public fkUserId!: number | null;

	/** `invoice.fk_company__id`. */
	@Column('int', { name: 'fk_company__id', unsigned: true })
	public fkCompanyId!: number;

	/** `invoice.type` as stored (1 invoice, 2 credit memo); no sign is flipped. */
	@Column('varchar', { name: 'type', length: 10, nullable: true })
	public type!: string | null;

	/** 1 when the invoice is cancelled (`invoice.status` = '4'). */
	@Column({ type: 'boolean', name: 'isCancelled', width: 1, default: () => "'0'" })
	public isCancelled!: boolean;

	/**
	 * 1 when the line's invoice counts as a sales invoice: its total without
	 * its non-sales lines is not zero (`invoiceHasSalesSql`), the invoice
	 * filter most sales reports apply to the lines they add up.
	 */
	@Column({ type: 'boolean', name: 'hasSales', width: 1, default: () => "'0'" })
	public hasSales!: boolean;

	/**
	 * The origin of the order the line was invoiced from ('1', '2', '3'), or
	 * 'unknown' for lines with no live order (ERP-imported lines), the same
	 * buckets as Sales by Origin.
	 */
	@Column('varchar', { name: 'originCode', length: 16 })
	public originCode!: string;

	/** SUM(invoice_detail.total). */
	@Column('decimal', { name: 'lineTotal', nullable: true, precision: 23, scale: 2 })
	public lineTotal!: string | null;

	/** SUM(invoice_detail.subtotal): the product sales every sales report sums. */
	@Column('decimal', { name: 'subtotal', nullable: true, precision: 23, scale: 2 })
	public subtotal!: string | null;

	/** SUM(invoice_detail.totalGP) over every line, known cost or not. */
	@Column('decimal', { name: 'grossProfit', nullable: true, precision: 23, scale: 2 })
	public grossProfit!: string | null;

	/** PD-16937 — the subtotal of the lines whose cost is known: the GP % base. */
	@Column('decimal', {
		name: 'knownCostSubtotal',
		nullable: true,
		precision: 23,
		scale: 2,
	})
	public knownCostSubtotal!: string | null;

	/** PD-16937 — the gross profit of the lines whose cost is known. */
	@Column('decimal', {
		name: 'knownCostGrossProfit',
		nullable: true,
		precision: 23,
		scale: 2,
	})
	public knownCostGrossProfit!: string | null;

	/**
	 * SUM(COALESCE(sellCost, acquisitionCost) * quantity): the cost of the
	 * known-cost lines. Scale 10 is the scale of that product, so no line's
	 * cost is rounded before it is added up.
	 */
	@Column('decimal', {
		name: 'costTimesQuantity',
		nullable: true,
		precision: 35,
		scale: 10,
	})
	public costTimesQuantity!: string | null;

	/** SUM(invoice_detail.discount), as stored (its unit is the line's discountType). */
	@Column('decimal', { name: 'discount', nullable: true, precision: 31, scale: 10 })
	public discount!: string | null;

	/** SUM(invoice_detail.quantity). */
	@Column('decimal', { name: 'quantity', nullable: true, precision: 25, scale: 4 })
	public quantity!: string | null;

	/** SUM(invoice_detail.price), for averages (see {@link priceCount}). */
	@Column('decimal', { name: 'priceSum', nullable: true, precision: 25, scale: 6 })
	public priceSum!: string | null;

	/** The number of invoice lines in the row. */
	@Column('int', { name: 'lineCount', unsigned: true })
	public lineCount!: number;

	/**
	 * COUNT(invoice_detail.price): the lines with a price, so an average price
	 * is `priceSum / priceCount`, as AVG() over the lines would be.
	 */
	@Column('int', { name: 'priceCount', unsigned: true, default: 0 })
	public priceCount!: number;

	/**
	 * The number of invoices in the row. Not additive across rows: one invoice
	 * appears in a row per item.
	 */
	@Column('int', { name: 'invoiceCount', unsigned: true })
	public invoiceCount!: number;

	/**
	 * The invoices this row counts toward its item: each invoice once per
	 * item, in the row of the first origin its lines of that item came from.
	 * Adding it up per item (and any invoice-level column: customer, rep,
	 * company, day) gives the exact number of invoices that sold the item,
	 * where adding up `invoiceCount` would count twice an invoice whose lines
	 * of the item came from orders of two origins.
	 */
	@Column('int', { name: 'itemInvoiceCount', unsigned: true, default: 0 })
	public itemInvoiceCount!: number;

	/**
	 * The highest invoice id in the row. With the date it names a customer's
	 * latest invoice (latest date, then highest id), whose rep the reports
	 * show on the customer's row.
	 */
	@Column('int', { name: 'maxInvoiceId', unsigned: true })
	public maxInvoiceId!: number;
}
